import axios from "axios";

// Actuator endpoints don't need JWT auth - they're separate from /api
const actuator = axios.create({
  baseURL: "/actuator",
  timeout: 5000,
});

export interface HealthStatus {
  status: "UP" | "DOWN" | "UNKNOWN";
  components?: Record<
    string,
    {
      status: "UP" | "DOWN" | "UNKNOWN";
      details?: Record<string, unknown>;
    }
  >;
}

export interface MetricValue {
  name: string;
  description: string;
  baseUnit: string | null;
  measurements: { statistic: string; value: number }[];
  availableTags: { tag: string; values: string[] }[];
}

export interface SystemMetrics {
  cpuUsage: number; // 0-100
  memoryUsed: number; // bytes
  memoryMax: number; // bytes
  diskFree: number; // bytes
  diskTotal: number; // bytes
  uptime: number; // seconds
  threadsLive: number;
  httpRequestsCount: number;
}

export const healthService = {
  async getHealth(): Promise<HealthStatus> {
    const response = await actuator.get<HealthStatus>("/health");
    return response.data;
  },

  async getMetricValue(name: string): Promise<number> {
    try {
      const response = await actuator.get<MetricValue>(`/metrics/${name}`);
      const measurement = response.data.measurements.find(
        (m) => m.statistic === "VALUE" || m.statistic === "COUNT",
      );
      return measurement?.value ?? 0;
    } catch {
      return 0;
    }
  },

  async getSystemMetrics(): Promise<SystemMetrics> {
    const [
      cpuUsage,
      memoryUsed,
      memoryMax,
      diskFree,
      diskTotal,
      uptime,
      threadsLive,
      httpRequestsCount,
    ] = await Promise.all([
      this.getMetricValue("system.cpu.usage"),
      this.getMetricValue("jvm.memory.used"),
      this.getMetricValue("jvm.memory.max"),
      this.getMetricValue("disk.free"),
      this.getMetricValue("disk.total"),
      this.getMetricValue("process.uptime"),
      this.getMetricValue("jvm.threads.live"),
      this.getMetricValue("http.server.requests"),
    ]);

    return {
      cpuUsage: Math.round(cpuUsage * 10000) / 100, // convert 0-1 to 0-100
      memoryUsed,
      memoryMax,
      diskFree,
      diskTotal,
      uptime,
      threadsLive,
      httpRequestsCount,
    };
  },

  formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  },

  formatUptime(seconds: number): string {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  },
};
